import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiHeart,
  FiMinus,
  FiPlus,
  FiShare2,
} from "react-icons/fi";
import productData, {
  formatCurrency,
  siteConfig,
} from "../Datas/productData";
import "../css/productdetails.scss";
import { CartContext } from "./CartContext";

const formatAttributeLabel = (label) =>
  label
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ")
    .replace(/^./, (character) => character.toUpperCase());

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, toggleWishlist, isInWishlist } = useContext(CartContext);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [shareNotice, setShareNotice] = useState("");

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return productData
      .filter((item) => item.id !== product.id)
      .sort((a, b) => {
        const aMatches = a.category === product.category ? 0 : 1;
        const bMatches = b.category === product.category ? 0 : 1;
        return aMatches - bMatches || a.id - b.id;
      })
      .slice(0, 3);
  }, [product]);

  useEffect(() => {
    setSelectedImage(0);
    setQuantity(1);
    setIsImageLoading(true);
    document.title = product
      ? `${product.name} — ${siteConfig.brandName}`
      : `Product not found — ${siteConfig.brandName}`;
  }, [product]);

  if (!product) {
    return (
      <main className="product-not-found" id="main-content">
        <p className="eyebrow">A little paper trail went cold</p>
        <h1>Product not found</h1>
        <p>This item may have moved, but plenty of delightful things remain.</p>
        <Link className="button" to="/shop">
          Back to shop
        </Link>
      </main>
    );
  }

  const handleAddToCart = () => {
    addToCart(product.id, quantity);
    navigate("/cartpage");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleShare = async () => {
    const shareData = {
      title: product.name,
      text: product.description,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(window.location.href);
        setShareNotice("Link copied");
      } else {
        setShareNotice("Copy the page address to share");
      }
    } catch {
      setShareNotice("");
    }
  };

  return (
    <main className="product-detail" id="main-content">
      <div className="product-detail__container">
        <div className="product-detail__breadcrumbs">
          <Link to="/shop">Shop</Link>
          <span>/</span>
          <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
            {product.category}
          </Link>
          <span>/</span>
          <span>{product.name}</span>
        </div>

        <div className="product-detail__layout">
          <header className="product-heading">
            <p className="product-category">{product.category}</p>
            <h1>{product.name}</h1>
            <p className="product-price">{formatCurrency(product.price)}</p>
          </header>

          <section className="gallery-column" aria-label="Product images">
            {product.images.length > 1 && (
              <div className="thumbs">
                {product.images.map((image, index) => (
                  <button
                    type="button"
                    key={image}
                    className={`thumb ${
                      index === selectedImage ? "is-active" : ""
                    }`}
                    onClick={() => {
                      setSelectedImage(index);
                      setIsImageLoading(true);
                    }}
                    aria-label={`Show image ${index + 1}`}
                  >
                    <img
                      src={image}
                      alt=""
                      loading={index === 0 ? "eager" : "lazy"}
                    />
                  </button>
                ))}
              </div>
            )}

            <div
              className={`main-image ${isImageLoading ? "is-loading" : ""}`}
            >
              <span className="image-skeleton" aria-hidden="true" />
              <img
                src={product.images[selectedImage]}
                alt={product.name}
                onLoad={() => setIsImageLoading(false)}
                onError={() => setIsImageLoading(false)}
              />
            </div>
          </section>

          <aside className="product-info">
            <div className="product-purchase">
              <div className="quantity-control" aria-label="Quantity">
                <button
                  type="button"
                  onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                  aria-label="Decrease quantity"
                >
                  <FiMinus />
                </button>
                <input
                  aria-label="Product quantity"
                  inputMode="numeric"
                  min="1"
                  max="99"
                  type="number"
                  value={quantity}
                  onChange={(event) =>
                    setQuantity(
                      Math.min(99, Math.max(1, Number(event.target.value) || 1))
                    )
                  }
                />
                <button
                  type="button"
                  onClick={() => setQuantity((value) => Math.min(99, value + 1))}
                  aria-label="Increase quantity"
                >
                  <FiPlus />
                </button>
              </div>
              <button
                className="product-add-button"
                type="button"
                onClick={handleAddToCart}
              >
                Add to cart
                <FiArrowRight aria-hidden="true" />
              </button>
            </div>

            <button
              className={`product-wishlist ${
                isInWishlist(product.id) ? "is-active" : ""
              }`}
              type="button"
              onClick={() => toggleWishlist(product.id)}
              aria-pressed={isInWishlist(product.id)}
            >
              <FiHeart aria-hidden="true" />
              {isInWishlist(product.id)
                ? "Saved to your wishlist"
                : "Add to wishlist"}
            </button>

            <section className="product-details-table" aria-labelledby="details-heading">
              <h2 id="details-heading">Item details</h2>
              <dl>
                {Object.entries(product.attributes).map(([label, value]) => (
                  <div key={label}>
                    <dt>{formatAttributeLabel(label)}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
                <div>
                  <dt>Origin</dt>
                  <dd>Designed in the USA</dd>
                </div>
              </dl>
            </section>

            <p className="product-description">{product.description}</p>

            <div className="product-shipping-note">
              <span>Flat-rate U.S. shipping</span>
              <p>
                {formatCurrency(siteConfig.shipping.standardRate)} delivery or
                free standard shipping over{" "}
                {formatCurrency(siteConfig.shipping.freeThreshold)}.
              </p>
            </div>

            <div className="product-share">
              <button type="button" onClick={handleShare}>
                <FiShare2 aria-hidden="true" /> Share
              </button>
              <span aria-live="polite">{shareNotice}</span>
            </div>
          </aside>
        </div>
      </div>

      <section className="product-recommendations" aria-labelledby="also-like">
        <div className="section-title-rule">
          <h2 id="also-like">You may also like</h2>
        </div>
        <div className="product-recommendations__grid">
          {relatedProducts.map((item) => (
            <Link to={`/shopDetails/${item.id}`} key={item.id}>
              <img src={item.images[0]} alt={item.name} loading="lazy" />
              <span>{item.name}</span>
              <em>{formatCurrency(item.price)}</em>
            </Link>
          ))}
        </div>
      </section>

      <Link className="product-back-link" to="/shop">
        <FiArrowLeft aria-hidden="true" /> Back to all products
      </Link>
    </main>
  );
};

export default ProductDetail;
